import React, { useState } from "react";
import { Link } from "react-router-dom";
import "./Modelo.css";
import { Timer } from "lucide-react";
import { useAuth } from 'global/context/AuthProviderV2';

const dimensions = [
	{
		title: "Customer Readiness Level (CRL)",
		description: "Nivel de madurez de validación con clientes",
		longDescription: "Evalúa la preparación del proyecto en relación con su base de clientes. Mide el nivel de comprensión, validación y adopción de la solución por parte del mercado objetivo, incluyendo el grado de interacción con usuarios, la validación del problema y solución, y el avance hacia ventas o compromisos comerciales.",
	},
	{
		title: "Technology Readiness Level (TRL)",
		description: "Nivel de madurez de desarrollo tecnológico",
		longDescription: "Evalúa el estado de desarrollo de la tecnología del proyecto. Va desde la investigación básica y el desarrollo de prototipos hasta la validación en entornos operativos reales. Indica cuán cerca está la tecnología de ser implementada comercialmente.",
	},
	{
		title: "Business Readiness Level (BRL)",
		description: "Nivel de madurez del modelo de negocio",
		longDescription: "Madurez del modelo de negocio del proyecto. Evalúa si el proyecto tiene un modelo de negocio sólido y viable, incluyendo la propuesta de valor, el mercado objetivo, la estrategia de ingresos y la capacidad para escalar.",
	},
	{
		title: "Team Readiness Level (TmRL)",
		description: "Nivel de madurez del equipo de trabajo",
		longDescription: "Mide las competencias, preparación y alineación del equipo del proyecto. Considera la experiencia y habilidades de los miembros del equipo, así como su capacidad para trabajar juntos de manera efectiva y abordar los desafíos del proyecto.",
	},
	{
		title: "Funding Readiness Level (FRL)",
		description: "Nivel de madurez del financiamiento",
		longDescription: "Esta dimensión mide la preparación del proyecto para obtener financiamiento. Evalúa la capacidad del equipo para identificar, asegurar y gestionar los fondos necesarios para llevar el proyecto al mercado y escalar.",
	},
	{
		title: "Intellectual Property Readiness Level (IPRL)",
		description: "Nivel de madurez en propiedad intelectual",
		longDescription: "Esta dimensión busca medir el estado de desarrollo de la estrategia de propiedad intelectual del proyecto. Evalúa la identificación, protección y gestión de los derechos de propiedad intelectual, como patentes, marcas y derechos de autor.",
	},
];

export default function ModeloDeslogeado() {
	const [expandedDimension, setExpandedDimension] = useState<string | null>(null);
	const { isAuthenticated } = useAuth();

	const handleToggleDimension = (title: string) => {
		setExpandedDimension((current) => (current === title ? null : title));
	};

	return (
		<main className="mdl-landing">
			<section className="mdl-hero">
				<div className="mdl-container mdl-hero-grid">
					<div className="mdl-hero-brand">
						<img
							src="/landing-assets/irl-logo.svg"
							alt="Calculadora IRL - Madurez de Proyectos de Innovación y EBCT"
						/>
					</div>

					<div className="mdl-hero-copy">
						<p>
							Mide con precisión el estado de madurez de innovación de un proyecto 
							tecnológico (tecnología) o una empresa de base científica-tecnológica. 
							Esta evaluación considera un análisis multidimensional en aspectos como 
							clientes, modelo de negocio, tecnología, equipo, financiamiento y propiedad 
							intelectual.
						</p>
						<p>
							La Calculadora IRL basada en el modelo <strong>The KTH Innovation Readiness Level™</strong> es 
							una iniciativa liderada originalmente por HUBTEC, que se desarrolló en 
							colaboración con Know Hub y APTA, en el marco del programa de Hubs de 
							Transferencia Tecnológica de ANID. Esta herramienta está disponible de manera 
							gratuita para la comunidad científica, tecnológica y de innovación.
						</p>
						<p className="mdl-estimated-time">
							<Timer aria-hidden="true" />
							<strong>Tiempo estimado: 15 - 25 minutos.</strong>
						</p>
					</div>
				</div>
			</section>

			<section className="mdl-steps">
				<div className="mdl-container">
					<h1 className="mdl-section-title">¿Cómo usar la calculadora? Sigue estos pasos</h1>

					<img
						className="fullWidthImg"
						src="/images/go/Proceso.png"
						alt="Pasos para usar la calculadora: responde, analizamos y recibe"
					/>
					<div className="mdl-steps-grid">

					</div>
				</div>
			</section>

			<section className="mdl-cta">
				{
					isAuthenticated ?
						<div className="mdl-container">
							<Link className="mdl-cta-button" to="/proyectos">
								COMIENZA LA EVALUACIÓN
							</Link>
	
						</div>
						:
						<div className="mdl-container">
							<Link className="mdl-cta-button" to="/login">
								COMIENZA LA EVALUACIÓN
							</Link>
							<div className="mdl-cta-copy">
								<p>
									<Link className="mdl-inline-link" to="/recuperar">
										<strong>¿Olvidaste tu contraseña?</strong>
									</Link>
								</p>
								<p>
									Si aún no te registras,{" "}
									<Link className="mdl-inline-link" to="/registro">
										<strong>ingresa aquí</strong>
									</Link>
								</p>
							</div>
						</div>
				}

			</section>

			<section className="mdl-details">
				<div className="mdl-container mdl-details-grid">
					<article className="mdl-details-box">
						<h2>Conoce las dimensiones del modelo</h2>

						{dimensions.map((dimension) => (
							<div key={dimension.title} className="mdl-dimension">
								<strong>{dimension.title}</strong>
								<button
									type="button"
									className="mdl-dimension-toggle"
									onClick={() => handleToggleDimension(dimension.title)}
									aria-expanded={expandedDimension === dimension.title}
								>
									<span
										className={`mdl-dimension-arrow ${expandedDimension === dimension.title ? "expanded" : ""
											}`}
										aria-hidden="true"
									/>
									<span className="mdl-dimension-label">{dimension.description}</span>
								</button>
								{expandedDimension === dimension.title && (
									<div className="mdl-dimension-content">{dimension.longDescription}</div>
								)}
							</div>
						))}
					</article>

					<article className="mdl-model-box">
						<img
							src="/landing-assets/kth-model.png"
							alt="Visualización del modelo KTH Innovation Readiness Level"
						/>
						<h2>Más información sobre el modelo KTH IRL</h2>
						<p>
							La evaluación y el informe generado se basa en el modelo internacional KTH Innovation Readiness
							Level, desarrollado por KTH Innovation bajo la licencia Creative Commons Attribution Non Commercial
							Share Alike 4.0 International (CC BY-NC-SA 4.0). El{" "}
							<a href="https://kthinnovationreadinesslevel.com/" target="_blank" rel="noreferrer">
								modelo completo
							</a>{" "}
							es accesible de manera pública.
						</p>
					</article>
				</div>
			</section>
		</main>
	);
}
